<template>
  <section v-reveal class="max-w-7xl mx-auto px-4 py-12">
    <SectionHeading
      :eyebrow="heading.eyebrow"
      :title="heading.title"
      :subtitle="heading.subtitle"
      compact
      class="mb-6"
    />

    <div
      v-if="mode === 'embedded' && embedUrl"
      class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
      <iframe
        :src="embedUrl"
        class="h-[28rem] w-full border-0"
        loading="lazy"
        referrerpolicy="no-referrer-when-cross-origin"
        allowfullscreen
        title="Recenzii Google"
      />
    </div>

    <div
      v-else-if="mode === 'api' && reviews.length"
      class="relative"
      @mouseenter="pauseAutoplay"
      @mouseleave="resumeAutoplay"
    >
      <div
        ref="trackRef"
        class="reviews-track flex gap-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory px-0.5 pb-1 touch-pan-x select-none"
        :class="{ 'is-dragging': dragActive }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @pointerleave="onPointerLeave"
      >
        <article
          v-for="(item, index) in reviews"
          :key="reviewKey(item, index)"
          data-review-card
          class="review-card snap-start shrink-0 w-[calc(100%-0.25rem)] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)] h-52 rounded-xl border border-default bg-default p-4 shadow-sm flex flex-col"
        >
          <div class="mb-3 flex items-center gap-2">
            <div
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700"
            >
              {{ item.authorName.charAt(0).toUpperCase() }}
            </div>
            <div class="min-w-0">
              <p class="truncate text-sm font-medium text-highlighted">{{ item.authorName }}</p>
              <p v-if="item.relativeTime" class="truncate text-xs text-dimmed">{{ item.relativeTime }}</p>
            </div>
          </div>

          <p class="mb-3 shrink-0 text-sm leading-none tracking-wide text-yellow-400" :aria-label="`${item.rating} din 5 stele`">
            <span v-for="i in item.rating" :key="`full-${i}`">★</span><span v-for="i in (5 - item.rating)" :key="`empty-${i}`" class="text-dimmed">★</span>
          </p>

          <p class="line-clamp-4 text-sm leading-relaxed text-toned">{{ item.text }}</p>
        </article>
      </div>
    </div>

    <div v-if="businessUrl" class="mt-6 text-center">
      <UButton
        label="Vezi toate recenziile pe Google"
        :to="businessUrl"
        target="_blank"
        rel="noopener noreferrer"
        variant="ghost"
        trailing-icon="i-heroicons-arrow-top-right-on-square"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import type { GoogleReviewItem } from '~/utils/googleReviews'
import type { GoogleReviewsMode } from '~/utils/googleReviewsDisplay'
import type { HomepageSectionHeading } from '~/utils/homepageSections'

const props = defineProps<{
  mode: GoogleReviewsMode
  reviews: GoogleReviewItem[]
  embedUrl?: string
  heading: HomepageSectionHeading
  businessUrl?: string
}>()

const visibleCount = 3
const autoplayDelayMs = 3000

const trackRef = ref<HTMLElement | null>(null)
const isPaused = ref(false)
const dragActive = ref(false)

let autoplayTimer: ReturnType<typeof setInterval> | null = null
let dragStartX = 0
let dragStartScrollLeft = 0
let dragPointerId: number | null = null
let dragMoved = false

function reviewKey(item: GoogleReviewItem, index: number) {
  return `${item.authorName}-${item.relativeTime ?? ''}-${index}`
}

function prefersReducedMotion(): boolean {
  if (!import.meta.client) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function isFinePointer(): boolean {
  if (!import.meta.client) return true
  return window.matchMedia('(pointer: fine)').matches
}

function getScrollStep(el: HTMLElement): number {
  const card = el.querySelector<HTMLElement>('[data-review-card]')
  if (!card) return el.clientWidth
  const styles = getComputedStyle(el)
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '16') || 16
  return card.offsetWidth + gap
}

function scrollToPosition(left: number, smooth = false) {
  const el = trackRef.value
  if (!el) return

  const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth)
  const target = Math.min(Math.max(left, 0), maxScroll)
  const behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto'

  el.scrollTo({ left: target, behavior })
}

function snapToNearest(smooth = true) {
  const el = trackRef.value
  if (!el) return

  const step = getScrollStep(el)
  if (step <= 0) return

  const index = Math.round(el.scrollLeft / step)
  scrollToPosition(index * step, smooth)
}

function scrollNext() {
  const el = trackRef.value
  if (!el || isPaused.value || dragActive.value) return

  const step = getScrollStep(el)
  const maxScroll = el.scrollWidth - el.clientWidth
  if (maxScroll <= 0) return

  const smooth = isFinePointer()
  if (el.scrollLeft >= maxScroll - 2) {
    scrollToPosition(0, smooth)
    return
  }

  scrollToPosition(el.scrollLeft + step, smooth)
}

function startAutoplay() {
  stopAutoplay()
  if (props.reviews.length <= visibleCount) return
  if (prefersReducedMotion()) return

  autoplayTimer = setInterval(scrollNext, autoplayDelayMs)
}

function stopAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer)
    autoplayTimer = null
  }
}

function pauseAutoplay() {
  isPaused.value = true
}

function resumeAutoplay() {
  isPaused.value = false
}

function endDrag(shouldSnap = true) {
  const el = trackRef.value
  if (!el) return

  if (dragPointerId != null && el.hasPointerCapture(dragPointerId)) {
    el.releasePointerCapture(dragPointerId)
  }

  dragActive.value = false
  dragPointerId = null

  if (shouldSnap && dragMoved) {
    snapToNearest(true)
  }

  dragMoved = false
  resumeAutoplay()
}

function onPointerDown(event: PointerEvent) {
  const el = trackRef.value
  if (!el || event.button !== 0) return

  dragActive.value = true
  dragMoved = false
  dragStartX = event.clientX
  dragStartScrollLeft = el.scrollLeft
  dragPointerId = event.pointerId
  pauseAutoplay()

  el.setPointerCapture(event.pointerId)
}

function onPointerMove(event: PointerEvent) {
  if (!dragActive.value || dragPointerId !== event.pointerId || !trackRef.value) return

  const delta = event.clientX - dragStartX
  if (Math.abs(delta) > 4) {
    dragMoved = true
    event.preventDefault()
  }

  trackRef.value.scrollLeft = dragStartScrollLeft - delta
}

function onPointerUp(event: PointerEvent) {
  if (!dragActive.value || dragPointerId !== event.pointerId) return
  endDrag(true)
}

function onPointerLeave(event: PointerEvent) {
  if (!dragActive.value || dragPointerId !== event.pointerId) return
  if (event.buttons !== 0) return
  endDrag(true)
}

onMounted(() => {
  startAutoplay()
})

onBeforeUnmount(() => {
  stopAutoplay()
})

watch(() => props.reviews.length, () => {
  startAutoplay()
})
</script>

<style scoped>
.reviews-track {
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
}

.reviews-track::-webkit-scrollbar {
  display: none;
}

@media (pointer: fine) {
  .reviews-track {
    cursor: grab;
  }

  .reviews-track.is-dragging {
    cursor: grabbing;
    scroll-snap-type: none;
  }
}
</style>
